import React, { useEffect, useState } from 'react';
import CreateOrderPage from './pages/CreateOrder';
import TrackOrderPage from './pages/TrackOrder';
import TrackRobuxPage from './pages/TrackRobux';
import LandingPage from './pages/Landing';
import { I18nProvider } from './i18n';

/**
 * An order ID alone doesn't say which product it is, so the tracker asks the
 * API before mounting a page. `/meta` reads KV only — no provider round-trip —
 * so this costs a few milliseconds and saves mounting the wrong flow, which
 * would fire the wrong polling and render the wrong instructions.
 *
 * Anything unknown falls back to the coins tracker: every order that predates
 * the Robux line is a coins order, and that page already handles a 404 itself.
 */
const TrackRouter: React.FC<{ customerName: string }> = ({ customerName }) => {
  const [product, setProduct] = useState<'fc_coins' | 'robux' | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/orders/${encodeURIComponent(customerName)}/meta`);
        const d = await res.json().catch(() => ({}));
        if (!cancelled) setProduct(d?.product === 'robux' ? 'robux' : 'fc_coins');
      } catch {
        if (!cancelled) setProduct('fc_coins');
      }
    })();
    return () => { cancelled = true; };
  }, [customerName]);

  if (!product) return null;
  return product === 'robux'
    ? <TrackRobuxPage customerName={customerName} />
    : <TrackOrderPage customerName={customerName} />;
};

const App: React.FC = () => {
  const segments = window.location.pathname.split('/').filter(Boolean);
  const first = segments[0];

  // The employee panel stays English; only customer-facing pages are translated.
  if (first === 'create') return <CreateOrderPage />;

  return (
    <I18nProvider>
      {first
        ? <TrackRouter customerName={decodeURIComponent(first)} />
        // Root → order-ID entry page (never expose the private /create page)
        : <LandingPage />}
    </I18nProvider>
  );
};

export default App;
